import { Toaster } from "react-hot-toast"
import Header from "../Header"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import useAuthStore from "../../../store/Auth"
import { useGetOfficeAccessAuthorizationById } from "../../../hooks/permit/query"
import Loader from "../../../components/loader"
import EmployeeInformation from "./EmployeeInformation"
import useFormWithSchema from "../../../hooks/useFormWithSchema"
import { OfficeAccessAuthorizationSchema } from "../../../schemas/officeAccessAuthorizationSchema"
import { useGetEmployeeDetails } from "../../../hooks/common/query"
import { useEffect, useMemo, useState } from "react"
import { isCheckPermitApprovedByNextApprover, isPermitFieldsDisabled } from '../../../lib/functions'
import { APP_ROUTES } from '../../../routes'
import RequestDetails from "./RequestDetails"
import ActionButtonGroup from "./ActionButtonGroup"

const OfficeAccessAuthorization = () => {
    const { t } = useTranslation()

    const location = useLocation()

    const { user } = useAuthStore()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const [requestedServicesIds, setRequestedServicesIds] = useState<number[]>([]);

    const { data: officeAccessAuthorization, isPending: isOfficeAccessAuthorizationLoading } =
        useGetOfficeAccessAuthorizationById({
            encryptedId: key || "",
        });

    const { data: employeeDetails } = useGetEmployeeDetails()

    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
    } = useFormWithSchema(OfficeAccessAuthorizationSchema);

    useEffect(() => {
        if (employeeDetails) {
            setValue("name", employeeDetails.name || "");
            setValue("department", employeeDetails.department || "");
            setValue("mobileNo", employeeDetails.mobileNo || "");
            setValue("employeeNumber", employeeDetails.employeeNumber || "");
        }
    }, [employeeDetails, setValue]);

    useEffect(() => {
        if (!officeAccessAuthorization && employeeDetails) {
            setValue("name", employeeDetails.name || "");
            setValue("department", employeeDetails.department || "");
            setValue("mobileNo", employeeDetails.mobileNo || "");
            setValue("employeeNumber", employeeDetails.employeeNumber || "");
            setValue("officeNumber", "");

            setRequestedServicesIds([])
            return
        }
        if (officeAccessAuthorization) {
            setValue("name", officeAccessAuthorization.s_EmployeeName || "");
            setValue("department", officeAccessAuthorization.s_Department || "");
            setValue("mobileNo", officeAccessAuthorization.s_MobileNumber || "");
            setValue("employeeNumber", officeAccessAuthorization.s_EmployeeNumber || "");
            setValue("officeNumber", officeAccessAuthorization.s_OfficeNumber || "");

            setRequestedServicesIds(officeAccessAuthorization.requestedServices?.map((service: any) => service.n_RequestedServiceID));
        }
    }, [officeAccessAuthorization, employeeDetails, setValue]);

    const isPermitApprovedByNextApprover = useMemo(
        () => isCheckPermitApprovedByNextApprover(location.pathname, APP_ROUTES.UPDATE_OFFICE_ACCESS_AUTHORIZATION, officeAccessAuthorization?.n_CurrentApproverOrderNo),
        [location.pathname, officeAccessAuthorization?.n_CurrentApproverOrderNo]
    );

    const isFieldsDisabled = useMemo(
        () => isPermitFieldsDisabled(officeAccessAuthorization, isPermitApprovedByNextApprover, location.pathname, APP_ROUTES.APPROVE_OFFICE_ACCESS_AUTHORIZATION, user?.n_RoleID),
        [isPermitApprovedByNextApprover, location.pathname, user?.n_RoleID, officeAccessAuthorization]
    );

    return (
        <>
            <Toaster position="top-right" reverseOrder={false} />

            <div className="flex flex-col gap-5">
                <Header title={t("officeAccessAuthorization")} data={officeAccessAuthorization} />

                {key && isOfficeAccessAuthorizationLoading ? (
                    <div className="flex justify-center">
                        <Loader />
                    </div>
                ) : (
                    <>
                        <EmployeeInformation
                            control={control}
                            errors={errors}
                            employeeDetails={employeeDetails}
                            isFieldsDisabled={isFieldsDisabled}
                        />

                        <RequestDetails
                            control={control}
                            errors={errors}
                            isFieldsDisabled={isFieldsDisabled}
                            requestedServicesIds={requestedServicesIds}
                            setRequestedServicesIds={setRequestedServicesIds}
                        />

                        <ActionButtonGroup
                            officeAccessAuthorization={officeAccessAuthorization}
                            isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
                            requestedServicesIds={requestedServicesIds}
                            isFieldsDisabled={isFieldsDisabled}
                            handleSubmit={handleSubmit}
                        />
                    </>
                )}
            </div>
        </>
    )
}

export default OfficeAccessAuthorization
